import { useTranslation } from 'next-i18next';
import { cx } from '@emotion/css';

import { Dealer, PreferenceType } from '../dealer.types';
import { useDealerPreferences } from '../hooks/use-dealer-preferences.hook';
import { useDealerMapContext } from '../context/dealer-map-context';
import { DealerPreferencesChip } from './dealer-preferences-chip';
import { useGtmTracking } from '../hooks/use-gtm-tracking';
import { DSButton } from '@stihl-design-system/components';

const DealerPreferenceActionBar = ({
  dealer,
  shouldDeselectDealerAfterRemoval = false,
  hideSelectAsMyDealerButton = false,
  editable = false,
  showInline = false,
}: {
  dealer?: Dealer;
  hideSelectAsMyDealerButton?: boolean;
  shouldDeselectDealerAfterRemoval?: boolean;
  editable?: boolean;
  showInline?: boolean;
}) => {
  const { t } = useTranslation(['shared']);
  const { setPresentSetAsMainDealerModal } = useDealerMapContext();
  const { removeDealerPreference, isLoadingRemove, isLoadingAdd, addDealerAsMain, addDealerAsFavorite, hasMainDealer } =
    useDealerPreferences();
  const { trackPromotion } = useGtmTracking();

  if (!dealer) {
    return null;
  }

  return (
    <div className={cx('flex flex-wrap gap-4', !showInline && 'w-full')} data-testid="dealer-details_preference-action-bar">
      {dealer.preference?.type === PreferenceType.MAIN && (
        <DealerPreferencesChip
          className="border border-primary-orange-dark border-1 text-primary-black bg-primary-orange-light"
          onClick={() => {
            removeDealerPreference(dealer, shouldDeselectDealerAfterRemoval);
            trackPromotion(dealer, 'remove-favorite-main');
          }}
          isLoading={isLoadingRemove(dealer.id)}
          data-testid="dealer-details_preferences-chip-myDealer"
          editable={editable}
          isMainDealer={true}
          dealerDescription={dealer.description}
        >
          {t('shared_dealers_map_list_chip_myDealer')}
        </DealerPreferencesChip>
      )}

      {dealer.preference?.type === PreferenceType.FAVORITE && (
        <DealerPreferencesChip
          className="bg-primary-black text-primary-white"
          onClick={() => {
            removeDealerPreference(dealer, shouldDeselectDealerAfterRemoval);
            trackPromotion(dealer, 'remove-favorite');
          }}
          isLoading={isLoadingRemove(dealer.id)}
          data-testid="dealer-details_preferences-chip-favDealer"
          editable={editable}
          isMainDealer={false}
          dealerDescription={dealer.description}
        >
          {t('shared_dealers_map_list_chip_fav')}
        </DealerPreferencesChip>
      )}

      {!dealer.preference?.type && editable && (
        <DSButton
          type="button"
          size="small"
          variant="outline"
          className="mobileOnly:w-full mobileOnly:grow inline-flex"
          iconName="star"
          iconPosition="left"
          loading={isLoadingAdd(dealer.id)}
          onClick={(event) => {
            event?.stopPropagation();
            event?.nativeEvent.stopImmediatePropagation();

            if (!hasMainDealer() && dealer?.preference?.type !== PreferenceType.MAIN) {
              setPresentSetAsMainDealerModal?.(dealer?.id);
            } else {
              addDealerAsFavorite(dealer?.id);
              trackPromotion(dealer, 'add-favorite');
            }
          }}
          data-testid="dealer-details_preferences-button-addToFav"
          aria-label={t('shared_dealers_map_list_button_addToFav_aria', {
            dealer: dealer.description,
          })}
        >
          {t('shared_dealers_map_list_button_addToFav')}
        </DSButton>
      )}
      {!hideSelectAsMyDealerButton && dealer.preference?.type !== PreferenceType.MAIN && editable && (
        <DSButton
          type="button"
          size="small"
          variant="outline"
          className="mobileOnly:w-full mobileOnly:grow inline-flex"
          loading={isLoadingAdd(dealer.id)}
          iconName="shop"
          iconPosition="left"
          onClick={(event) => {
            event?.preventDefault();
            event?.nativeEvent.stopImmediatePropagation();
            addDealerAsMain(dealer?.id);
            trackPromotion(dealer, 'add-favorite-main');
          }}
          data-testid="dealer-details_preferences-button-setAsMyDealer"
          aria-label={t('shared_dealers_map_detail_button_selectAsMyDealer_aria', {
            dealer: dealer.description,
          })}
        >
          {t('shared_dealers_map_detail_button_selectAsMyDealer')}
        </DSButton>
      )}
    </div>
  );
};

export { DealerPreferenceActionBar };
